/**
 * Lightweight analytics helper for the app.
 * Uses the exported `analytics` instance from `src/firebase.js` and
 * wraps `logEvent` to avoid throwing when analytics is not available
 * (e.g., server-side rendering or unsupported environments).
 *
 * Example:
 *   import { trackEvent } from '@/services/analytics'
 *   trackEvent('purchase', { value: 9.99 })
 */
import { logEvent, setUserId } from 'firebase/analytics'
import { onAuthStateChanged, signInAnonymously, signOut } from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { analytics as analyticsInstance, auth, db, initAnalytics } from '../firebase'

// Local storage key for opt-in preference
const PREF_KEY = 'analytics_opt_in'

function readPref() {
  try {
    const v = localStorage.getItem(PREF_KEY)
    if (v === null) return null
    return v === 'true'
  } catch (err) {
    return null
  }
}

function writePref(value) {
  try {
    localStorage.setItem(PREF_KEY, value ? 'true' : 'false')
  } catch (err) {
    // ignore
  }
}

/**
 * Logs an analytics event if analytics is available.
 * @param {string} eventName - The name of the event to log
 * @param {Object} [params] - Optional parameters for the event
 */
export function trackEvent(eventName, params = {}) {
  if (!eventName || typeof eventName !== 'string') {
    console.warn('[analytics] Invalid event name:', eventName)
    return
  }

  // Respect opt-in/out preference if set. If not set, default to enabled in production.
  const pref = readPref()
  const defaultEnabled = process.env.NODE_ENV === 'production'
  const enabled = pref === null ? defaultEnabled : pref
  if (!enabled) {
    console.info('[analytics] Analytics disabled by user preference. Event skipped:', eventName)
    return
  }

  if (!analyticsInstance) {
    // Analytics not initialized yet. Try to initialize (consent must have been given earlier)
    // Queue this event to be sent once init completes.
    initAnalytics()
      .then((inst) => {
        if (!inst) {
          console.info('[analytics] Analytics not available after init. Event skipped:', eventName)
          return
        }

        try {
          if (auth?.currentUser?.uid) {
            try { setUserId(inst, auth.currentUser.uid) } catch (_) { void 0 }
          }
          logEvent(inst, eventName, safeParams)
        } catch (err) {
          console.warn('[analytics] Failed to log queued event:', err)
        }
      })
      .catch((err) => {
        console.warn('[analytics] initAnalytics failed while queuing event', err)
      })

    return
  }

  // Fire-and-forget: ensure anonymous user exists so analytics can be associated
  // We don't await here to keep the API synchronous for callers; the
  // background sign-in will set the analytics user id when available.
  ensureAnonymousUser().catch(() => { void 0 })

  // Sanitize params to avoid sending PII accidentally
  const safeParams = sanitizeParams(params)

  try {
    // If auth already has a user and analytics is available, set the user id
    if (auth?.currentUser?.uid) {
      try {
        setUserId(analyticsInstance, auth.currentUser.uid)
      } catch (err) {
        // not critical
      }
    }

    logEvent(analyticsInstance, eventName, safeParams)
  } catch (err) {
    console.warn('[analytics] Failed to log event:', err)
  }
}

export default { trackEvent }

/** Opt-in, opt-out and query functions */
export function enableAnalytics() {
  writePref(true)
  // Initialize analytics SDK after explicit opt-in
  try {
    if (typeof window !== 'undefined') {
      initAnalytics().catch((e) => {
        console.warn('[analytics] initAnalytics failed', e)
      })
    }
  } catch (err) {
    console.warn('[analytics] enableAnalytics init failed', err)
  }
}

export function disableAnalytics() {
  writePref(false)
  // Attempt to stop collection and clear identifiers where possible
  try {
    // If analytics SDK exposes resetAnalyticsData, call it.
    // We access the instance exported from firebase.js (analyticsInstance)
    if (typeof window !== 'undefined' && analyticsInstance) {
      try {
        // Some SDKs expose resetAnalyticsData on the global analytics object
        if (typeof analyticsInstance.resetAnalyticsData === 'function') {
          analyticsInstance.resetAnalyticsData()
        }
      } catch (e) {
        // ignore if not available
      }
    }

    // Sign out of anonymous auth so any anonymous user id isn't reused
    if (auth && auth.currentUser && auth.currentUser.isAnonymous) {
      try {
        signOut(auth).catch(() => { })
      } catch (e) {
        // noop
      }
    }
  } catch (err) {
    console.warn('[analytics] disableAnalytics cleanup failed', err)
  }
}

/**
 * Basic sanitizer to avoid sending common PII shapes to analytics.
 * - removes values that look like emails or long numeric sequences
 * - shallow copies objects and strips keys that may contain PII
 */
function sanitizeParams(params) {
  if (!params || typeof params !== 'object') return {}
  const out = {}
  for (const key of Object.keys(params)) {
    const val = params[key]
    if (typeof val === 'string') {
      const v = val.trim()
      // email-like
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        out[key] = '[redacted_email]'
        continue
      }
      // long numeric sequence (possible ID, card, or national id)
      if (/^\d{6,}$/.test(v)) {
        out[key] = '[redacted_number]'
        continue
      }
      // default safe string (truncate to protect accidental PII)
      out[key] = v.length > 200 ? v.slice(0, 200) : v
    } else if (typeof val === 'number' || typeof val === 'boolean') {
      out[key] = val
    } else if (val && typeof val === 'object') {
      // shallow stringify but avoid nested PII; keep keys only
      try {
        out[key] = JSON.stringify(val, Object.keys(val).slice(0, 5))
      } catch (_) {
        out[key] = '[object]'
      }
    } else {
      out[key] = val
    }
  }
  return out
}

export function isAnalyticsEnabled() {
  const pref = readPref()
  if (pref === null) return process.env.NODE_ENV === 'production'
  return !!pref
}

/**
 * Server-side / global analytics control (super-admin override)
 * Stores a document at /settings/analytics with { enabled: boolean }
 */
export async function getGlobalAnalyticsSetting() {
  try {
    if (!db) return null
    const ref = doc(db, 'settings', 'analytics')
    const snap = await getDoc(ref)
    if (!snap.exists()) return null
    const data = snap.data()
    return typeof data.enabled === 'boolean' ? data.enabled : null
  } catch (err) {
    console.warn('[analytics] failed to read global analytics setting', err)
    return null
  }
}

export async function setGlobalAnalyticsSetting(value) {
  try {
    if (!db) throw new Error('firestore-not-available')
    // Security: client must have rights (rules must allow admin set). We still try and let rules enforce.
    const ref = doc(db, 'settings', 'analytics')
    await setDoc(ref, { enabled: !!value, updatedAt: new Date(), updatedBy: auth?.currentUser?.uid || null }, { merge: true })
    return true
  } catch (err) {
    console.warn('[analytics] failed to write global analytics setting', err)
    return false
  }
}

// --- Anonymous tracking helpers ---
let _anonInitPromise = null

/**
 * Ensure the client is signed in anonymously. If already signed in (including
 * with a named provider) this is a no-op. Returns a promise that resolves
 * when an anonymous session is established or immediately if not needed.
 */
export function ensureAnonymousUser() {
  if (!auth) return Promise.resolve(null)

  // If already have a user, nothing to do
  if (auth.currentUser) return Promise.resolve(auth.currentUser)

  // Reuse a single inflight promise
  if (_anonInitPromise) return _anonInitPromise

  _anonInitPromise = new Promise((resolve) => {
    // Try to sign in anonymously. In many cases the app may have other
    // providers enabled; anonymous sign-in will only succeed if the project
    // allows anonymous accounts. Errors are non-fatal for analytics.
    signInAnonymously(auth)
      .then((cred) => {
        resolve(cred.user)
      })
      .catch((err) => {
        // If anonymous sign-in is disabled or fails, just resolve with null.
        console.info('[analytics] anonymous sign-in failed or disabled:', err && err.code ? err.code : err)
        resolve(null)
      })
      .finally(() => {
        // clear the inflight promise so future calls can retry
        _anonInitPromise = null
      })
  })

  return _anonInitPromise
}

// Listen for auth state changes and set analytics user id when available.
if (auth && typeof window !== 'undefined') {
  try {
    onAuthStateChanged(auth, (user) => {
      // If analytics is not yet initialized, try to init; otherwise set user id
      if (!analyticsInstance) {
        initAnalytics()
          .then((inst) => {
            if (!inst) return
            try { if (user && user.uid) setUserId(inst, user.uid) } catch (_) { void 0 }
          })
          .catch(() => { void 0 })
        return
      }

      try {
        if (user && user.uid) setUserId(analyticsInstance, user.uid)
      } catch (err) {
        // ignore analytics setUserId errors
      }
    })
  } catch (err) {
    // noop
  }
}
